import React, { useState, useRef, useEffect } from 'react';

// A small custom dropdown that allows multi-line option labels and fixed width list.
// Props:
// - value: string
// - options: [{ value: string, label: string }]
// - placeholder: string
// - onChange: fn(value)
// - disabled: boolean
// - className: extra classes for the button

export default function WrappingSelect({ value, options = [], placeholder = 'Select...', onChange = () => {}, disabled = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));

  return (
    <div className={`relative inline-block w-full ${disabled ? 'opacity-60 pointer-events-none' : ''}`} ref={ref}>
      <button
        type="button"
        className={`w-full text-left border rounded px-3 py-2 bg-white flex items-center justify-between ${className}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/></svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border rounded shadow-lg p-1"
          style={{ whiteSpace: 'normal' }}
        >
          {options.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">No options</li>
          )}
          {options.map((opt, idx) => (
            <li
              key={String(opt.value) || idx}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 rounded ${String(opt.value) === String(value) ? 'bg-gray-100 font-medium' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{ whiteSpace: 'normal' }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
